// L'etat du PANIER d'impression : les commandes que Simon a cochees dans la
// liste pour les imprimer en UNE SEULE commande Print.com, et donc payer UN
// SEUL transport au lieu de plusieurs.
//
// Simon a decide que le panier survit a tout, rechargement de page compris. Il
// vit donc dans le localStorage du navigateur.
//
// AUCUNE importation, ni React ni Supabase : ces regles doivent pouvoir etre
// prouvees sans monter un composant. (Meme raison que src/impression.js.)

// Le numero de version est DANS le nom de la boite. Le jour ou la forme du
// panier change, on change ce nom : les anciens paniers seront alors ignores,
// au lieu d'etre relus de travers.
export const CLE_STOCKAGE = "incendieplan.panier.impression.v1";

// Mesure en base le 2026-10-02 : une cle peut etre ABSENTE de l'objet, pas
// seulement vide. Tester === "" laisserait passer undefined. Vide, absente ou
// null valent donc tous les trois « non renseigne ». (Copie de impression.js :
// ce fichier n'importe rien, c'est voulu.)
function texte(valeur) {
  if (valeur === null || valeur === undefined) return "";
  return String(valeur).trim();
}

// Le seul statut qui autorise l'impression. Le mot est recopie de
// src/constants.js au lieu d'etre importe : ce fichier ne doit rien importer.
// Si ce mot change un jour, il change aux DEUX endroits.
const STATUT_VALIDE = "Validé";

// « Validé » peut s'ecrire de deux facons en Unicode : le « é » d'un seul
// caractere, ou « e » suivi d'un accent separe. Les deux s'affichent pareil et
// ne sont PAS egaux pour ===. On ramene les deux a la meme forme : sinon une
// commande bel et bien validee sortirait du panier en silence, ce que toute
// cette fonction existe pour empecher.
function memeMot(a, b) {
  return a.normalize("NFC") === b.normalize("NFC");
}

// Un panier, c'est une liste d'identifiants : des textes non vides, sans
// doublon, dans l'ordre ou Simon les a coches. Tout le reste est jete.
function nettoyer(ids) {
  if (!Array.isArray(ids)) return [];
  const vus = new Set();
  const propres = [];
  for (const brut of ids) {
    // Un nombre, un null, un objet : rien de tout cela n'est un identifiant.
    if (typeof brut !== "string") continue;
    const id = brut.trim();
    if (!id) continue;
    if (vus.has(id)) continue;
    vus.add(id);
    propres.push(id);
  }
  return propres;
}

// Lit le panier garde dans le navigateur. Tout ce qui n'est pas une liste de
// textes revient a un panier VIDE : un stockage abime ne doit pas casser la
// liste des commandes.
export function lire(stockage) {
  let brut = null;
  try {
    // getItem lui-meme peut JETER : Safari en navigation privee, un cookie
    // bloque par le navigateur. On n'essaie meme pas de distinguer, on avale.
    brut = stockage && stockage.getItem(CLE_STOCKAGE);
  } catch (e) {
    return [];
  }
  if (!brut) return [];
  try {
    // JSON.parse jette sur du texte abime. nettoyer() s'occupe du reste : un
    // objet, un nombre ou null donnent une liste vide sans jamais jeter.
    return nettoyer(JSON.parse(brut));
  } catch (e) {
    return [];
  }
}

// Ecrit. Un stockage indisponible (navigation privee, quota) ne doit JAMAIS
// casser l'ecran : on avale l'erreur et on continue en memoire.
// Rend la liste nettoyee, justement pour que l'appelant garde en memoire
// exactement ce qu'on a essaye d'ecrire, meme quand le stockage a refuse.
export function ecrire(stockage, ids) {
  const propres = nettoyer(ids);
  try {
    if (stockage) stockage.setItem(CLE_STOCKAGE, JSON.stringify(propres));
  } catch (e) {
    // Rien a faire ici, et c'est le but : l'ecran passe avant le stockage.
  }
  return propres;
}

// Les verrous arrivent soit en Set, soit en tableau : l'appelant lit la table
// de liens et n'a pas a choisir une forme. On accepte LES DEUX.
function ensembleVerrous(commandesVerrouillees) {
  const verrous = new Set();
  if (!commandesVerrouillees) return verrous;
  // Un Set comme un tableau se parcourent tous deux avec for...of.
  const source =
    typeof commandesVerrouillees.forEach === "function" ? commandesVerrouillees : [];
  for (const brut of source) {
    const id = texte(brut);
    if (id) verrous.add(id);
  }
  return verrous;
}

// LA REGLE DE SECURITE DU § 4.3. Un panier garde VIEILLIT.
// Rend { ids, retires } : les ids encore valables, et pour chaque id ecarte,
// la RAISON, en francais, prete a afficher. Jamais de retrait silencieux.
//
// IL Y A QUATRE RAISONS, ET SEULEMENT QUATRE :
//   1. la commande n'est plus dans la liste visible a cet administrateur
//   2. son statut n'est plus « Validé »
//   3. elle est deja prise dans une impression (presente dans la table de liens)
//   4. elle est archivee
//
// 🚫 « IMPRIMABLE » N'EN FAIT PAS PARTIE, ET C'EST VOULU. Un plan sans matiere,
// sans orientation, ou dont le couple matiere+format n'est pas configure, ne
// quitte PAS le panier en silence : il fabrique un refus NOMME, calcule par le
// serveur, qui bloque tout le panier. Retirer sans bruit une commande que Simon
// croit avoir commandee, c'est exactement le mal que cette fonction empeche.
//
// L'ordre des quatre controles est FIXE, et le premier qui repond gagne : une
// commande ecartee porte UNE raison, toujours la meme pour la meme situation.
export function revalider(ids, commandes, commandesVerrouillees) {
  const demandes = nettoyer(ids);
  if (demandes.length === 0) return { ids: [], retires: [] };

  // Rangees par identifiant TEXTE : en base un id peut etre un nombre, et
  // "7" doit retrouver la commande 7.
  const parId = new Map();
  if (Array.isArray(commandes)) {
    for (const c of commandes) {
      if (!c || typeof c !== "object") continue;
      const id = texte(c.id);
      if (id && !parId.has(id)) parId.set(id, c);
    }
  }

  const verrous = ensembleVerrous(commandesVerrouillees);
  const gardes = [];
  const retires = [];

  for (const id of demandes) {
    const commande = parId.get(id);

    // 1. Plus dans la liste. Soit elle a ete supprimee, soit cet
    // administrateur n'y a plus acces : de son point de vue, c'est pareil.
    if (!commande) {
      retires.push({ id, ref: "", raison: "n'existe plus ou n'est plus visible" });
      continue;
    }

    const ref = texte(commande.ref);

    // 2. Plus validee. On NOMME le nouveau statut : sans lui, Simon ne sait pas
    // si la commande a recule d'une etape ou si quelqu'un l'a rouverte.
    const statut = texte(commande.statut);
    if (!memeMot(statut, STATUT_VALIDE)) {
      retires.push({
        id,
        ref,
        // Un statut vide ou absent arrive : on le dit, on ne l'invente pas.
        raison: "n'est plus validée (statut : " + (statut || "inconnu") + ")",
      });
      continue;
    }

    // 3. Deja prise dans une impression. C'est le verrou de la table de liens :
    // sans lui, le meme plan partirait deux fois chez Print.com, et serait paye
    // deux fois.
    if (verrous.has(id)) {
      retires.push({ id, ref, raison: "déjà prise dans une impression" });
      continue;
    }

    // 4. Archivee. is_archived vaut parfois null en base : seul le vrai `true`
    // archive, pour qu'un champ jamais rempli n'archive pas tout.
    if (commande.is_archived === true) {
      retires.push({ id, ref, raison: "archivée" });
      continue;
    }

    gardes.push(id);
  }

  // L'ordre est celui dans lequel Simon a coche les cases. On ne retrie jamais :
  // des cases qui changent de place sous le doigt, c'est un clic a cote.
  return { ids: gardes, retires };
}
